import type { WeightType } from '@jira/polaris__domain-field/src/OptionFieldValue.types.tsx';
import type {
	FieldConfiguration,
	FieldKey,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { createGetFieldByKey } from './createGetFieldByKey.tsx';
import { DEFAULT_OPTION_WEIGHT, type Props, type State } from './FieldsUpdate.types.tsx';

export const updateFieldOptionWeightType =
	(fieldKey: FieldKey, newOptionWeightType: WeightType | undefined) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	): Promise<Error | undefined> => {
		const field = createGetFieldByKey(fieldKey)(getState());
		if (!field) {
			return undefined;
		}

		const oldConfiguration: FieldConfiguration | undefined = field.configuration;
		const newConfiguration: FieldConfiguration = {
			...field.configuration,
			optionWeightType: newOptionWeightType,
		};

		const oldOptions = field.options;
		let newOptions = field.options;
		const isWeightingRemoved = newOptionWeightType === undefined;
		if (isWeightingRemoved) {
			newConfiguration.optionWeights = (field.configuration?.optionWeights || []).map((e) => ({
				...e,
				weight: DEFAULT_OPTION_WEIGHT,
			}));
			newOptions = newOptions?.map((optionItem) => ({
				...optionItem,
				weight: DEFAULT_OPTION_WEIGHT,
			}));
		}

		try {
			const fields = { ...getState().fields };
			fields[fieldKey] = {
				...fields[fieldKey],
				configuration: newConfiguration,
				options: newOptions,
			};
			setState({ fields });

			await fieldRemote.updateFieldConfiguration({
				oldConfiguration: oldConfiguration ?? {},
				configuration: newConfiguration,
				fieldKey: field.key,
				type: 'updateFieldOptionWeightType',
			});

			fireTrackAnalytics(createAnalyticsEvent({}), 'issueFields updated', {
				issueFieldKey: fieldKey,
				issueFieldType: field.type,
				updatedItems: Object.keys(newConfiguration).map((name) => ({ name })),
			});

			return undefined;
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			const fields = { ...getState().fields };
			fields[fieldKey] = {
				...fields[fieldKey],
				configuration: oldConfiguration,
				options: oldOptions,
			};
			setState({ fields });

			onFieldUpdateFailed(err);

			return err;
		}
	};
